/**
 * FinderCrosswordPage — daily-rotating mini crossword.
 *
 * Fully client-side. A small library of hand-crafted Aussie-themed
 * crosswords is stored inline; day-of-year picks today's puzzle so it
 * changes every day without a backend. Grid + clues render on-screen
 * and print cleanly on A4.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";

// ── Puzzle library. Each puzzle is a rectangular grid where `null`
// is a black square and a string letter is a solvable cell. Clues
// list the word start (row, col), direction and clue text.
//
// Author's note: grids are 7×7 mini-crosswords — big enough to feel
// like a real crossword, small enough to solve in ~3 minutes on a
// phone in a car park. All answers are lowercase in code; rendered
// uppercase.
const PUZZLES = [
  {
    id: "au-01",
    title: "Aussie Icons",
    grid: [
      ["k","o","a","l","a",null,null],
      ["a",null,null,null,"y","e","s"],
      ["n",null,"e","m","u",null,"u"],
      ["g","u","m",null,null,"o","n"],
      ["a",null,"u",null,null,"p",null],
      ["r","e","e","f",null,"a",null],
      ["o",null,null,"i","s","l","e"],
    ],
    clues: {
      across: [
        { num: 1, row: 0, col: 0, answer: "koala", clue: "Grey marsupial that clings to gum trees" },
        { num: 4, row: 1, col: 4, answer: "yes",   clue: "Affirmative reply" },
        { num: 6, row: 2, col: 2, answer: "emu",   clue: "Flightless bird on the coat of arms" },
        { num: 7, row: 3, col: 0, answer: "gum",   clue: "Eucalypt, informally" },
        { num: 9, row: 5, col: 0, answer: "reef",  clue: "Great Barrier ___" },
        { num: 11, row: 6, col: 3, answer: "isle", clue: "Small island" },
      ],
      down: [
        { num: 1, row: 0, col: 0, answer: "kangaroo", clue: "Hopping national emblem" },
        { num: 2, row: 2, col: 2, answer: "emu",     clue: "Same as 6-Across" },
        { num: 3, row: 1, col: 4, answer: "yu",      clue: "Middle of 'ayu' (Japanese fish)" },
        { num: 5, row: 1, col: 6, answer: "sun",     clue: "It always shines on Bondi" },
        { num: 8, row: 3, col: 5, answer: "opal",    clue: "National gemstone" },
        { num: 10, row: 5, col: 3, answer: "fi",     clue: "Wi-___" },
      ],
    },
  },
  {
    id: "au-02",
    title: "On the Road",
    grid: [
      ["r","o","a","d",null,"u","p"],
      ["u",null,null,"r","o","t","e"],
      ["l","o","o",null,null,"e",null],
      ["e",null,"i","c","e","d",null],
      [null,null,"l",null,"m",null,null],
      ["h","u","m","p",null,"c","d"],
      [null,null,"y","o","g","a",null],
    ],
    clues: {
      across: [
        { num: 1, row: 0, col: 0, answer: "road", clue: "The thing you're on" },
        { num: 3, row: 0, col: 5, answer: "up",   clue: "Opposite of down" },
        { num: 4, row: 1, col: 3, answer: "rote", clue: "Learning by ___" },
        { num: 6, row: 2, col: 0, answer: "loo",  clue: "Aussie word for toilet" },
        { num: 7, row: 3, col: 2, answer: "iced", clue: "Chilled with cubes" },
        { num: 9, row: 5, col: 0, answer: "hump", clue: "Wednesday's other name (___ day)" },
        { num: 10, row: 5, col: 5, answer: "cd",  clue: "Silver disc, 700 MB" },
        { num: 11, row: 6, col: 2, answer: "yoga",clue: "Stretchy road-trip stop" },
      ],
      down: [
        { num: 1, row: 0, col: 0, answer: "rule", clue: "Regulation" },
        { num: 2, row: 0, col: 2, answer: "oil",  clue: "Check the ___ before you leave" },
        { num: 5, row: 1, col: 5, answer: "tec",  clue: "Detective, informally" },
        { num: 8, row: 3, col: 4, answer: "em",   clue: "Punctuation dash" },
        { num: 12, row: 5, col: 3, answer: "po",  clue: "Post-office abbreviation" },
      ],
    },
  },
  {
    id: "au-03",
    title: "At the Beach",
    grid: [
      ["s","a","n","d",null,"s","u"],
      ["u",null,null,"i","c","e","p"],
      ["r","e","e","f",null,"a",null],
      ["f",null,null,null,"b","l","u"],
      [null,"c","r","a","b",null,"p"],
      ["t","o","w","e","l",null,null],
      [null,"o","d","d",null,"h","i"],
    ],
    clues: {
      across: [
        { num: 1, row: 0, col: 0, answer: "sand", clue: "It's between your toes" },
        { num: 3, row: 0, col: 5, answer: "su",   clue: "Prefix meaning below" },
        { num: 4, row: 1, col: 3, answer: "ice",  clue: "In the esky" },
        { num: 6, row: 1, col: 6, answer: "up",   clue: "Where the sun is at noon" },
        { num: 7, row: 2, col: 0, answer: "reef", clue: "Coral formation" },
        { num: 9, row: 3, col: 4, answer: "blu",  clue: "Blueprint prefix" },
        { num: 10, row: 4, col: 1, answer: "crab",clue: "Sideways-walking crustacean" },
        { num: 11, row: 5, col: 0, answer: "towel",clue: "You lie on it" },
        { num: 13, row: 6, col: 1, answer: "odd", clue: "Not even" },
        { num: 14, row: 6, col: 5, answer: "hi",  clue: "Casual greeting" },
      ],
      down: [
        { num: 1, row: 0, col: 0, answer: "surf",  clue: "Ride the waves" },
        { num: 2, row: 0, col: 5, answer: "seal",  clue: "Aquatic mammal, aka Neil" },
        { num: 5, row: 1, col: 3, answer: "if",    clue: "Conditional word" },
        { num: 8, row: 2, col: 4, answer: "bb",    clue: "'Beach ball' initials" },
        { num: 12, row: 5, col: 0, answer: "to",   clue: "Direction preposition" },
      ],
    },
  },
];

// Pick today's puzzle by day-of-year → rotates automatically.
function todaysPuzzle() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const doy = Math.floor((now - start) / 86400000);
  return PUZZLES[doy % PUZZLES.length];
}

// Build a lookup of {row,col} → number for the number badges.
function numberGrid(grid) {
  const rows = grid.length;
  const cols = grid[0].length;
  const nums = {};
  let n = 1;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] == null) continue;
      const startsAcross =
        (c === 0 || grid[r][c - 1] == null) &&
        c + 1 < cols && grid[r][c + 1] != null;
      const startsDown =
        (r === 0 || grid[r - 1][c] == null) &&
        r + 1 < rows && grid[r + 1][c] != null;
      if (startsAcross || startsDown) {
        nums[`${r},${c}`] = n++;
      }
    }
  }
  return nums;
}

export default function FinderCrosswordPage() {
  const puzzle = useMemo(() => todaysPuzzle(), []);
  const rows = puzzle.grid.length;
  const cols = puzzle.grid[0].length;
  const nums = useMemo(() => numberGrid(puzzle.grid), [puzzle]);

  // player entries: 2D array of uppercase letters (or "" for blank).
  const [entries, setEntries] = useState(() =>
    puzzle.grid.map((row) => row.map((c) => (c == null ? null : "")))
  );
  const [sel, setSel] = useState({ row: 0, col: 0, dir: "across" });
  const [checked, setChecked] = useState(false);
  const [won, setWon] = useState(false);
  const refs = useRef({});

  useEffect(() => {
    document.title = "Daily Crossword — HustleFinder";
  }, []);

  // Focus first solvable cell on mount.
  useEffect(() => {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (puzzle.grid[r][c] != null) {
          setSel({ row: r, col: c, dir: "across" });
          const el = refs.current[`${r},${c}`];
          if (el) el.focus();
          return;
        }
      }
    }
  }, [puzzle, rows, cols]);

  const isBlack = (r, c) =>
    r < 0 || c < 0 || r >= rows || c >= cols || puzzle.grid[r][c] == null;

  const move = useCallback((r, c, dir) => {
    const dr = dir === "down" ? 1 : 0;
    const dc = dir === "across" ? 1 : 0;
    let nr = r + dr;
    let nc = c + dc;
    while (!isBlack(nr, nc)) {
      setSel({ row: nr, col: nc, dir });
      const el = refs.current[`${nr},${nc}`];
      if (el) el.focus();
      return;
    }
    // fall back: stay put
  }, [puzzle]);

  const setLetter = (r, c, letter) => {
    setEntries((prev) => {
      const next = prev.map((row) => row.slice());
      next[r][c] = letter.toUpperCase();
      return next;
    });
    setChecked(false);
  };

  const onKey = (r, c, e) => {
    const key = e.key;
    if (key === "ArrowRight") { e.preventDefault(); setSel({ ...sel, dir: "across" }); move(r, c, "across"); }
    else if (key === "ArrowLeft") {
      e.preventDefault();
      const nc = c - 1;
      if (!isBlack(r, nc)) { setSel({ row: r, col: nc, dir: "across" }); refs.current[`${r},${nc}`]?.focus(); }
    }
    else if (key === "ArrowDown") { e.preventDefault(); setSel({ ...sel, dir: "down" }); move(r, c, "down"); }
    else if (key === "ArrowUp") {
      e.preventDefault();
      const nr = r - 1;
      if (!isBlack(nr, c)) { setSel({ row: nr, col: c, dir: "down" }); refs.current[`${nr},${c}`]?.focus(); }
    }
    else if (key === "Backspace") {
      e.preventDefault();
      if (entries[r][c]) {
        setLetter(r, c, "");
      } else {
        // move back one cell in current direction
        const dr = sel.dir === "down" ? -1 : 0;
        const dc = sel.dir === "across" ? -1 : 0;
        if (!isBlack(r + dr, c + dc)) {
          setLetter(r + dr, c + dc, "");
          setSel({ row: r + dr, col: c + dc, dir: sel.dir });
          refs.current[`${r + dr},${c + dc}`]?.focus();
        }
      }
    }
    else if (/^[a-zA-Z]$/.test(key)) {
      e.preventDefault();
      setLetter(r, c, key);
      move(r, c, sel.dir);
    }
  };

  const check = () => {
    setChecked(true);
    let allRight = true;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (puzzle.grid[r][c] == null) continue;
        if ((entries[r][c] || "").toLowerCase() !== puzzle.grid[r][c]) {
          allRight = false;
        }
      }
    }
    setWon(allRight);
  };

  const reveal = () => {
    setEntries(
      puzzle.grid.map((row) => row.map((c) => (c == null ? null : (c || "").toUpperCase())))
    );
    setChecked(true);
    setWon(true);
  };

  const clear = () => {
    setEntries(puzzle.grid.map((row) => row.map((c) => (c == null ? null : ""))));
    setChecked(false);
    setWon(false);
  };

  const cellState = (r, c) => {
    if (isBlack(r, c)) return "black";
    if (!checked) return entries[r][c] ? "filled" : "empty";
    const correct = (entries[r][c] || "").toLowerCase() === puzzle.grid[r][c];
    return correct ? "correct" : "wrong";
  };

  return (
    <div
      data-testid="crossword-page"
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16"
    >
      <style>{`
        @media print { .no-print { display: none !important; } }
      `}</style>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="no-print flex items-center justify-between mb-4">
          <Link
            to="/finder/puzzles"
            data-testid="crossword-back-link"
            className="text-xs text-emerald-700 hover:underline"
          >
            ← back to Puzzles
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="crossword-print-btn"
            className="text-xs bg-slate-900 text-white px-3 py-1.5 rounded-full hover:bg-slate-800"
          >
            Print A4
          </button>
        </div>

        <h1 className="text-3xl font-bold text-slate-900 mb-1">
          Daily Crossword
        </h1>
        <p className="text-sm text-slate-500 mb-1">{puzzle.title}</p>
        <p className="text-xs text-slate-400 mb-5">
          Fresh puzzle every day · tap a cell then type
        </p>

        {won && (
          <div
            data-testid="crossword-won-banner"
            className="no-print bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-xl p-3 mb-4 text-sm font-semibold text-center"
          >
            Solved! Nice work.
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[auto_1fr]">
          {/* Grid */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 inline-block">
            <div
              className="grid"
              style={{
                gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                width: `${cols * 44}px`,
                maxWidth: "100%",
              }}
              data-testid="crossword-grid"
            >
              {puzzle.grid.map((row, r) =>
                row.map((_ch, c) => {
                  const state = cellState(r, c);
                  const isSel = sel.row === r && sel.col === c;
                  const num = nums[`${r},${c}`];
                  if (state === "black") {
                    return (
                      <div
                        key={`${r},${c}`}
                        className="aspect-square bg-slate-900 border border-slate-900"
                        data-testid={`crossword-cell-${r}-${c}-black`}
                      />
                    );
                  }
                  const bg =
                    state === "wrong"   ? "bg-red-100"    :
                    state === "correct" ? "bg-emerald-100":
                    isSel               ? "bg-yellow-100" : "bg-white";
                  return (
                    <div
                      key={`${r},${c}`}
                      className={`relative aspect-square border border-slate-300 ${bg}`}
                    >
                      {num && (
                        <span className="absolute top-0 left-0.5 text-[9px] text-slate-500 leading-none pt-0.5">
                          {num}
                        </span>
                      )}
                      <input
                        ref={(el) => { refs.current[`${r},${c}`] = el; }}
                        data-testid={`crossword-cell-${r}-${c}`}
                        value={entries[r][c] || ""}
                        onChange={() => { /* handled by onKey */ }}
                        onKeyDown={(e) => onKey(r, c, e)}
                        onFocus={() => setSel({ row: r, col: c, dir: sel.dir })}
                        onClick={() => setSel((s) => ({ row: r, col: c, dir: s.row === r && s.col === c ? (s.dir === "across" ? "down" : "across") : s.dir }))}
                        maxLength={1}
                        className="w-full h-full text-center text-lg font-bold text-slate-900 uppercase bg-transparent focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        inputMode="text"
                        autoCapitalize="characters"
                      />
                    </div>
                  );
                })
              )}
            </div>

            <div className="no-print flex gap-2 mt-3" data-testid="crossword-controls">
              <button
                onClick={check}
                data-testid="crossword-check-btn"
                className="flex-1 py-2 px-3 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
              >
                Check
              </button>
              <button
                onClick={clear}
                data-testid="crossword-clear-btn"
                className="py-2 px-3 rounded-lg bg-white border border-slate-200 text-sm hover:border-emerald-400"
              >
                Clear
              </button>
              <button
                onClick={reveal}
                data-testid="crossword-reveal-btn"
                className="py-2 px-3 rounded-lg bg-white border border-slate-200 text-sm text-slate-500 hover:border-red-300"
              >
                Reveal
              </button>
            </div>
          </div>

          {/* Clues */}
          <div className="text-sm">
            <h3 className="font-bold text-slate-900 mb-2 mt-2">Across</h3>
            <ul className="space-y-1 mb-5" data-testid="crossword-clues-across">
              {puzzle.clues.across.map((cl) => (
                <li key={`a-${cl.num}`} className="text-slate-700">
                  <span className="font-semibold text-slate-900">{cl.num}.</span>{" "}
                  {cl.clue}
                </li>
              ))}
            </ul>
            <h3 className="font-bold text-slate-900 mb-2">Down</h3>
            <ul className="space-y-1" data-testid="crossword-clues-down">
              {puzzle.clues.down.map((cl) => (
                <li key={`d-${cl.num}`} className="text-slate-700">
                  <span className="font-semibold text-slate-900">{cl.num}.</span>{" "}
                  {cl.clue}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
